import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { getCurrencySymbol } from '../utils/currencyUtils';
import { formatDate } from '../utils/dateUtils';

const RentDueModal = ({ show, onClose, tenancyId, onSave, rentDue = null }) => {
  const { agencyCurrency, user, agencyCountry } = useContext(AuthContext);
  const currencySymbol = getCurrencySymbol(agencyCurrency);
  const isEditing = !!rentDue;

  // Helper function for consistent date formatting
  const formatDateWithContext = (dateString) => {
    return formatDate(dateString, {
      userPreference: user?.dateFormatPreference,
      agencyCountry: agencyCountry
    });
  };

  // Form state
  const [formData, setFormData] = useState({
    periodStart: '',
    periodEnd: '',
    amount: ''
  });

  // For creating new rent due (original functionality)
  const [fromDate, setFromDate] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // Populate form when editing
  useEffect(() => {
    if (isEditing && rentDue) {
      const periodStart = rentDue.PeriodStart || rentDue.period_start;
      const periodEnd = rentDue.PeriodEnd || rentDue.period_end;

      // Extract amount from different possible formats
      let amountValue = '';
      if (rentDue.Amount?.amount) {
        amountValue = (rentDue.Amount.amount / 100).toString(); // Convert cents to dollars
      } else if (rentDue.amount?.amount) {
        amountValue = (rentDue.amount.amount / 100).toString();
      } else if (rentDue.Amount) {
        amountValue = rentDue.Amount.toString();
      } else if (rentDue.amount) {
        amountValue = rentDue.amount.toString();
      }

      setFormData({
        periodStart: periodStart ? periodStart.split('T')[0] : '',
        periodEnd: periodEnd ? periodEnd.split('T')[0] : '',
        amount: amountValue
      });
    } else {
      // Reset form for creating new rent due
      setFormData({
        periodStart: '',
        periodEnd: '',
        amount: ''
      });
      setFromDate('');
    }
  }, [isEditing, rentDue]);

  const handleCreateSubmit = (e) => {
    e.preventDefault();
    if (!fromDate) {
      setError('Please select a from date');
      return;
    }
    setShowConfirm(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    if (!formData.periodStart || !formData.periodEnd || !formData.amount) {
      setError('Please fill in all required fields');
      return;
    }

    if (parseFloat(formData.amount) <= 0) {
      setError('Amount must be greater than 0');
      return;
    }

    await handleUpdateRentDue();
  };

  const handleUpdateRentDue = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = sessionStorage.getItem('token') ||
                   sessionStorage.getItem('access_token') ||
                   localStorage.getItem('token') ||
                   localStorage.getItem('access_token');

      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api/v1';
      const rentDueId = rentDue.id || rentDue.ID;
      const url = `${baseUrl}/rent-dues/${rentDueId}`;

      // Convert dates to ISO format
      const periodStartISO = new Date(formData.periodStart).toISOString();
      const periodEndISO = new Date(formData.periodEnd).toISOString();

      // Convert amount to cents (integer)
      const amountInCents = Math.round(parseFloat(formData.amount) * 100);

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          period_start: periodStartISO,
          period_end: periodEndISO,
          amount: {
            amount: amountInCents,
            currency: agencyCurrency
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update rent due: ${response.statusText}`);
      }

      const data = await response.json();
      resetForm();
      onSave(data);
    } catch (err) {
      console.error('Error updating rent due:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateConfirm = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = sessionStorage.getItem('token') ||
                   sessionStorage.getItem('access_token') ||
                   localStorage.getItem('token') ||
                   localStorage.getItem('access_token');

      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api/v1';
      const url = `${baseUrl}/tenancies/${tenancyId}/rent-dues/from-start-date`;

      // Convert date to ISO 8601 format with time
      const dateObj = new Date(fromDate);
      const isoDate = dateObj.toISOString();

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          period_start: isoDate
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create rent due: ${response.statusText}`);
      }

      const data = await response.json();
      resetForm();
      onSave(data);
    } catch (err) {
      console.error('Error creating rent due:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      periodStart: '',
      periodEnd: '',
      amount: ''
    });
    setFromDate('');
    setShowConfirm(false);
    setError(null);
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  const handleFormDataChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!show) return null;

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {isEditing ? 'Edit Rent Due' : 'Add Rent Manually'}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              disabled={loading}
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            {error && (
              <div className="alert alert-danger alert-dismissible fade show" role="alert">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setError(null)}
                  aria-label="Close"
                ></button>
              </div>
            )}

            {isEditing ? (
              // Edit Form
              <form onSubmit={handleEditSubmit}>
                <div className="mb-3">
                  <label htmlFor="periodStart" className="form-label">
                    Period Start Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    id="periodStart"
                    value={formData.periodStart}
                    onChange={(e) => handleFormDataChange('periodStart', e.target.value)}
                    onKeyDown={(e) => e.preventDefault()}
                    required
                    disabled={loading}
                  />
                  {formData.periodStart && (
                    <small className="form-text fw-semibold text-primary">
                      {formatDateWithContext(formData.periodStart)}
                    </small>
                  )}
                </div>

                <div className="mb-3">
                  <label htmlFor="periodEnd" className="form-label">
                    Period End Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    id="periodEnd"
                    value={formData.periodEnd}
                    onChange={(e) => handleFormDataChange('periodEnd', e.target.value)}
                    onKeyDown={(e) => e.preventDefault()}
                    required
                    disabled={loading}
                  />
                  {formData.periodEnd && (
                    <small className="form-text fw-semibold text-primary">
                      {formatDateWithContext(formData.periodEnd)}
                    </small>
                  )}
                </div>

                <div className="mb-3">
                  <label htmlFor="amount" className="form-label">
                    Amount <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">{currencySymbol}</span>
                    <input
                      type="number"
                      className="form-control"
                      id="amount"
                      value={formData.amount}
                      onChange={(e) => handleFormDataChange('amount', e.target.value)}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>
              </form>
            ) : (
              // Create Form (original functionality)
              <>
                {showConfirm ? (
                  <div className="alert alert-warning">
                    <i className="bi bi-exclamation-circle me-2"></i>
                    Are you sure you want to create rent due from <strong>{formatDateWithContext(fromDate)}</strong>?
                    This action will generate a rent due entry.
                  </div>
                ) : (
                  <form onSubmit={handleCreateSubmit}>
                    <div className="mb-3">
                      <label htmlFor="fromDate" className="form-label">
                        From Date <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        id="fromDate"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        onKeyDown={(e) => e.preventDefault()}
                        required
                        disabled={loading}
                      />
                      {fromDate && (
                        <small className="form-text fw-semibold text-primary">
                          {formatDateWithContext(fromDate)}
                        </small>
                      )}
                      <div className="form-text">
                        Select the start date from which to generate rent due
                      </div>
                    </div>
                  </form>
                )}
              </>
            )}
          </div>
          <div className="modal-footer">
            {isEditing ? (
              // Edit Footer
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleEditSubmit}
                  disabled={loading || !formData.periodStart || !formData.periodEnd || !formData.amount}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Updating...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-1"></i>
                      Update Rent Due
                    </>
                  )}
                </button>
              </>
            ) : (
              // Create Footer (original functionality)
              <>
                {showConfirm ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowConfirm(false)}
                      disabled={loading}
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleCreateConfirm}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Creating...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-circle me-1"></i>
                          Confirm
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleClose}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleCreateSubmit}
                      disabled={loading || !fromDate}
                    >
                      Save
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentDueModal;

